import { CheckCircle, FileText, UserPlus, Calendar, MessageSquare, ShieldCheck } from 'lucide-react';
import { useEffect, useRef } from 'react';

const features = [
  {
    icon: <UserPlus className="h-10 w-10 text-taxby-purple" />,
    title: "Alta como autónomo",
    description: "Te acompañamos durante todo el proceso de alta, sin necesidad de acudir presencialmente a ninguna oficina."
  },
  {
    icon: <FileText className="h-10 w-10 text-taxby-purple" />,
    title: "Presentación de modelos tributarios",
    description: "Gestionamos la presentación de tus modelos fiscales trimestrales y anuales, incluyendo modelos 303, 130, 390, entre otros."
  },
  {
    icon: <CheckCircle className="h-10 w-10 text-taxby-orange" />,
    title: "Migración desde gestorías tradicionales",
    description: "Te ayudamos a migrar tu documentación y datos contables desde tu gestoría actual de forma rápida y segura."
  },
  {
    icon: <MessageSquare className="h-10 w-10 text-taxby-purple" />,
    title: "Asistencia profesional personalizada",
    description: "Contamos con un equipo de gestores expertos disponibles para resolver tus dudas fiscales y contables de forma clara y oportuna."
  },
  {
    icon: <ShieldCheck className="h-10 w-10 text-taxby-purple" />,
    title: "Plataforma segura y en la nube",
    description: "Accede a toda tu información contable en un entorno seguro y accesible desde cualquier dispositivo."
  }
];

const Features = () => {
  const featuresRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const handleScroll = () => {
      if (!featuresRef.current) return;
      const scrollPosition = window.scrollY;
      const featuresSectionTop = featuresRef.current.offsetTop;
      const featuresSectionHeight = featuresRef.current.offsetHeight;
      if (scrollPosition > featuresSectionTop - window.innerHeight && 
          scrollPosition < featuresSectionTop + featuresSectionHeight) {
        cardsRef.current.forEach((card, index) => {
          if (card) {
            const row = Math.floor(index / 3);
            const factor = row % 2 === 0 ? 0.03 : -0.03;
            const offset = (scrollPosition - featuresSectionTop) * factor;
            card.style.transform = `translateY(${offset}px)`;
          }
        });
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <section id="features" ref={featuresRef} className="section-padding bg-white relative parallax-container">
      <div className="container mx-auto">
        <div className="text-center mb-12">
          <span className="block text-taxby-purple font-semibold mb-4">Plataforma fiscal inteligente</span>
          <h2 className="text-3xl md:text-4xl font-bold text-taxby-violet mb-6">
            Todo lo que tu gestoría necesita — potenciado por IA
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Desde el alta como autónomo hasta la presentación de modelos: automatización, asesor IA y
            equipo profesional en una sola plataforma pensada para 2026.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index}
              ref={(el) => (cardsRef.current[index] = el)}
              className="bg-white rounded-xl p-6 shadow-lg border border-gray-100 hover:shadow-xl hover:border-taxby-purple/40 transition-all duration-300 parallax-element"
              style={{ 
                animationDelay: `${index * 0.1}s`,
                transform: 'translateY(0px)'
              }}
            >
              <div className="bg-taxby-purple/10 p-3 rounded-full inline-block mb-4">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold text-taxby-violet mb-4">{feature.title}</h3>
              <p className="text-gray-700">{feature.description}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Background shapes for parallax effect */}
      <div className="absolute -bottom-10 -right-10 h-64 w-64 bg-taxby-purple/10 rounded-full -z-10"></div>
      <div className="absolute top-40 left-20 h-40 w-40 bg-taxby-purple/10 rounded-full -z-10"></div>
    </section>
  );
};

export default Features;
